import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import Button from 'sushiweb/Button';
import Checkbox from 'sushiweb/InputTypes/Checkbox';
import ChevronLeft from 'sushiweb/Icons/all/ChevronLeft';
import Layout from 'sushiweb/Layout';

import {
  PageWrapper,
  PageHeaderContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SectionHeading,
  ContentContainer,
  FooterWrapper,
  FullFooter,
  ReviewActionsContainer,
  ReviewCheckboxContainer,
  PolicyHeaderLayout,
  CreateMealPlanButton,
} from '../../styles';

import { transformProgramInfoToPolicyProps } from '../../common/helpers/inputHandlers';
import PlanSnippet from '../../common/Components/PlanSnippet/PlanSnippet';
import PolicySnippet from '../../common/Components/PolicySnippet/PolicySnippet';
import StepBar from '../../common/Components/StepBar/StepBar';
import { createPlanSteps } from '../../common/Components/constants';
import { PROGRAMS_ROUTE } from '../../../../constants/routes';
import {
  getGroupsData,
  createPlanFormData,
  createProgramBody,
} from './util';

const ReviewPlan = ({
  companyId,
  currentFlow,
  selectedWorkspace,
  groupsData,
  programInfo,
  createProgram,
  getProgramInfo,
  creatingProgram,
  createProgramSuccess,
  clearProgramInfo,
}) => {
  const navigate = useNavigate();
  const [formChecked, setFormChecked] = useState(false);
  const [flag, setFlag] = useState(false);
  const [showInnerModal, setShowInnerModal] = useState(false);
  const { programId } = useParams();

  const { groups = [] } = groupsData;

  useEffect(() => {
    if (
      programId &&
      programId !== '' &&
      selectedWorkspace?.workspace_id
    ) {
      getProgramInfo({
        programId,
        companyId,
        workspaceId: selectedWorkspace?.workspace_id || '',
      });
    }
  }, [programId, companyId, selectedWorkspace?.workspace_id]);

  const { selectedGroups, unSelectedGroups } = getGroupsData(groups, programInfo);

  const planFormData = createPlanFormData(
    programInfo,
    selectedGroups,
    unSelectedGroups,
    selectedWorkspace,
    groups,
    currentFlow,
  );

  const goBack = () => {
    navigate(-1);
  };

  const handleCheckBox = () => {
    setFormChecked(!formChecked);
  };

  const handleCreateBtnClick = () => {
    if (!formChecked) return;
    const programBody = createProgramBody(selectedWorkspace, programId);
    setFlag(true);
    createProgram({ programBody, id: companyId });
  };

  useEffect(() => {
    if (creatingProgram) setFlag(true);
  }, [creatingProgram]);

  useEffect(() => {
    if (!creatingProgram && flag && createProgramSuccess) {
      clearProgramInfo();
      navigate(PROGRAMS_ROUTE);
    }
  }, [flag, creatingProgram, createProgramSuccess]);

  const policyPropsArray = transformProgramInfoToPolicyProps({
    apiData: programInfo,
    setEditingPolicy: () => {},
    setShowModal: () => {},
    deleteOption: () => {},
    isViewMode: true,
  });

  return (
    <PageWrapper>
      <PageHeaderContainer>
        <PageHeader>
          <PageTitle>
            {programInfo?.program_status === 0
              ? 'Create meal plan'
              : 'View meal plan'}
          </PageTitle>
          {programInfo?.program_status === 0 && (
            <PageSubtitle>Set up a new meal plan for your teams</PageSubtitle>
          )}
        </PageHeader>
        {programInfo?.program_status === 0 && (
          <StepBar steps={createPlanSteps} currentStep={3} />
        )}
      </PageHeaderContainer>
      <ContentContainer>
        <Layout.Item colD={8} colT={12} colM={12} style={{ textAlign: 'left' }}>
          <SectionHeading>Meal plan details</SectionHeading>
        </Layout.Item>
        <PlanSnippet
          formData={planFormData}
          updateFormData={() => {}}
          updateSelectedGroups={() => {}}
          updateUnSelectedGroups={() => {}}
          allGroups={groups}
          planDataFlag
        />
        <PolicyHeaderLayout>
          {policyPropsArray?.length > 0 && (
            <Layout.Item
              colD={6}
              colT={8}
              colM={8}
              style={{ textAlign: 'left' }}
            >
              <SectionHeading>Food policy details</SectionHeading>
            </Layout.Item>
          )}
        </PolicyHeaderLayout>
        {policyPropsArray?.map(policyProps => (
          <PolicySnippet
            key={policyProps?.policyTitle}
            policyTitle={policyProps?.policyTitle}
            buttons={policyProps?.buttons}
            applicableServices={policyProps?.applicableServices}
            applicableDays={policyProps?.applicableDays}
            budgetPerEmployee={policyProps?.budgetPerEmployee}
            cycleType={policyProps?.cycleType}
            slots={policyProps?.slots}
            policyQuestions={policyProps?.policyQuestions}
            onModalOpen={() => setShowInnerModal(true)}
            onModalClose={() => setShowInnerModal(false)}
          />
        ))}
      </ContentContainer>
      {!showInnerModal && (
        <FooterWrapper>
          <FullFooter>
            <Button
              appearance='outline'
              icon={<ChevronLeft />}
              iconAlign='left'
              onClick={goBack}
              disabled={creatingProgram}
            >
              Back
            </Button>
            {programInfo?.program_status === 0 && (
              <ReviewActionsContainer>
                <ReviewCheckboxContainer>
                  <Checkbox
                    checked={formChecked}
                    onChange={handleCheckBox}
                    color='#e23744'
                  />
                  <span>I have reviewed the meal plan details</span>
                </ReviewCheckboxContainer>
                <CreateMealPlanButton
                  btnColor='red'
                  onClick={handleCreateBtnClick}
                  loading={creatingProgram}
                  disabled={!formChecked}
                >
                  Create meal plan
                </CreateMealPlanButton>
              </ReviewActionsContainer>
            )}
          </FullFooter>
        </FooterWrapper>
      )}
    </PageWrapper>
  );
};

ReviewPlan.propTypes = {
  companyId: PropTypes.string,
  currentFlow: PropTypes.number,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  groupsData: PropTypes.objectOf(PropTypes.any),
  programInfo: PropTypes.objectOf(PropTypes.any),
  createProgram: PropTypes.func,
  getProgramInfo: PropTypes.func,
  creatingProgram: PropTypes.bool,
  createProgramSuccess: PropTypes.bool,
  clearProgramInfo: PropTypes.func,
};

ReviewPlan.defaultProps = {
  companyId: '',
  currentFlow: 0,
  selectedWorkspace: {},
  groupsData: {},
  programInfo: {},
  createProgram: _noop,
  getProgramInfo: _noop,
  creatingProgram: false,
  createProgramSuccess: false,
  clearProgramInfo: _noop,
};

export default ReviewPlan;
